import { TranslateService } from '@ngx-translate/core';
import { Component, Inject, OnInit } from '@angular/core';
import { ValidatorFn, AbstractControl, ValidationErrors, UntypedFormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReadFile } from 'app/directives/file-picker.directive';

@Component({
    selector: 'app-dialog-upload-file',
    templateUrl: './dialog-update-profile-picture.component.html',
    styleUrls: ['./dialog-update-profile-picture.component.scss']
})
export class DialogUpdateProfilePictureComponent implements OnInit {

    readonly MAX_IMAGE_SIZE_IN_KB: number = 500;
    readonly ACCEPTED_FILE_TYPES: string = 'image/png,image/jpg,image/jpeg';

    imageDisplay: string;
    image?: ReadFile;
    loadedImageControl: UntypedFormControl;

    private deleteGroupProfilePicture: boolean = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { currentImageUrl: string, defaultImageUrl: string },
        public dialogRef: MatDialogRef<DialogUpdateProfilePictureComponent>,
        private translate: TranslateService,
    ) { }

    get deletable() {
        return this.data.currentImageUrl !== this.data.defaultImageUrl;
    }

    get acceptedFileTypesDisplay() {
        return this.ACCEPTED_FILE_TYPES.replace(/image\//g, ' .');
    }

    ngOnInit(): void {
        this.loadedImageControl = new UntypedFormControl(null, [Validators.required, this.getImageSizeValidator(), this.getImageTypeValidator()]);
        this.imageDisplay = this.data.currentImageUrl;
    }

    getConfirmIsEnabled() {
        return this.loadedImageControl.valid || (this.deleteGroupProfilePicture && this.deletable);
    }

    loadImage(image: ReadFile): void {
        this.image = image;
        this.imageDisplay = this.image.content;
        this.loadedImageControl.setValue(image.name);
        this.loadedImageControl.markAsTouched();
        this.deleteGroupProfilePicture = false;
    }

    deleteImage() {
        this.image = null; // in case an image has been loaded but not submitted
        this.loadedImageControl.setValue(null); // same
        this.imageDisplay = this.data.defaultImageUrl;
        this.deleteGroupProfilePicture = true;
    }

    submit() {
        if (this.getConfirmIsEnabled()) {
            this.dialogRef.close({
                imageExtension: this.image?.name.split('.')[1],
                imageContent: this.image?.content.replace(/^data:image\/\w+;base64,/, ''),
                imageType: this.image?.type
            });
        }
    }

    getError(): string | null {
        const errors = this.loadedImageControl.errors;
        if (errors?.imageTooLarge) {
            return this.translate.instant('DIALOG_UPLOAD_FILE.INVALID_IMAGE_SIZE', { maxImageSize: this.MAX_IMAGE_SIZE_IN_KB });
        }
        if (errors?.imageType) {
            return this.translate.instant('DIALOG_UPLOAD_FILE.INVALID_IMAGE_TYPE', { acceptedType: this.acceptedFileTypesDisplay });
        }
    }

    private getImageSizeValidator(): ValidatorFn {
        return (_: AbstractControl): ValidationErrors | null => {
            return this.image?.size > (this.MAX_IMAGE_SIZE_IN_KB * 1000) ? { 'imageTooLarge': true } : null;
        };
    }

    private getImageTypeValidator(): ValidatorFn {
        return (_: AbstractControl): ValidationErrors | null => {
            return !this.image?.type || this.ACCEPTED_FILE_TYPES.includes(this.image.type) ? null : { 'imageType': true };
        };
    }
}

export type UpdateProfilePictureResult = {
    imageExtension: string;
    imageContent: string;
    imageType: string;
}
